/**
 * API 客户端。类型与后端契约手写镜像（与 up-lab 同一惯例），
 * 两端形状靠 review 保持一致，漂移会在运行时以 undefined 字段暴露。
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

const TOKEN_KEY = 'upoa_token';

const token = (): string => localStorage.getItem(TOKEN_KEY) ?? '';

/** 401 时统一回调：App 在这里把会话清掉、回到登录页。 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    headers: token() === '' ? {} : { Authorization: `Bearer ${token()}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 401) onUnauthorized?.();
    throw new ApiError(body.error ?? `HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, payload: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token() === '' ? {} : { Authorization: `Bearer ${token()}` }),
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    throw new ApiError(body.error ?? `HTTP ${res.status}`, res.status);
  }
  return body as T;
}

export interface AuthUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  /** 角色名（RBAC，app_role.name）。 */
  readonly role: string;
  /** 该角色的权限点集合，服务端每次现查 —— 改权限即时生效。 */
  readonly permissions: readonly string[];
  /** 为 true 时系统锁死在改密页，改完才能用（服务端强制，前端只负责渲染）。 */
  readonly mustChangePassword: boolean;
}

export interface AuthResult {
  readonly token: string;
  readonly expiresAt: string;
  readonly user: AuthUser;
}

// ── 会话与账号安全 ─────────────────────────────────────────

export interface SessionRow {
  readonly tokenFingerprint: string;
  readonly kind: 'web' | 'api';
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly createdIp: string | null;
  readonly userAgent: string | null;
}

export interface SessionList {
  readonly sessions: readonly SessionRow[];
  /** 当前令牌的指纹：与行内 tokenFingerprint 相同者即「本设备」。 */
  readonly currentFingerprint: string;
}

export type LoginLogEventKind =
  | 'login'
  | 'logout'
  | 'session_revoked'
  | 'password_changed'
  | 'account_disabled';

export interface LoginLogEntry {
  readonly id: string;
  readonly event: LoginLogEventKind;
  readonly method: 'password' | 'api_token' | null;
  readonly ip: string | null;
  readonly userAgent: string | null;
  readonly createdAt: string;
}

export interface ApiTokenRow {
  readonly tokenFingerprint: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

// ── 管理接口（RBAC）────────────────────────────────────────

export interface ManagedUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly role: string;
  readonly status: string;
  readonly createdAt: string;
  readonly permissions: readonly string[];
  /** 绑定的腾讯会议 userid（未绑定为 null）。 */
  readonly tencentUserId: string | null;
  /** 绑定的飞书 open_id（未绑定为 null）—— 绑定后能在「我的会议」里看到自己组织或参加的飞书会议。 */
  readonly feishuOpenId: string | null;
}

export interface RoleInfo {
  readonly name: string;
  readonly description: string;
}

export interface RoleRow {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly isSystem: boolean;
  readonly permissions: readonly string[];
  readonly userCount: number;
}

export interface PermissionDef {
  readonly key: string;
  readonly label: string;
  readonly description: string;
}

// ── 部门与部门负责人 ───────────────────────────────────────

export interface DepartmentMember {
  readonly userId: string;
  readonly username: string;
  readonly displayName: string;
  readonly role: string;
  readonly status: string;
  readonly isLeader: boolean;
  /** 外部通讯录里的部门归属（飞书），只作参考，不参与权限判定。 */
  readonly feishuDepartmentPath: string | null;
}

export interface DepartmentRow {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly memberCount: number;
  readonly leaderCount: number;
  readonly members: readonly DepartmentMember[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ConfigItemDef {
  readonly key: string;
  readonly label: string;
  readonly group: string;
  readonly type: 'number' | 'string';
  readonly unit?: string;
  readonly min?: number;
  readonly max?: number;
  readonly integer?: boolean;
  readonly allowed?: readonly string[];
  readonly effect: string;
  readonly absentValue: unknown;
}

export interface ConfigView {
  readonly items: readonly ConfigItemDef[];
  readonly values: Record<string, unknown>;
  readonly meta: Record<string, { updatedBy: string | null; updatedAt: string }>;
}

/** 一项汇总计数（label/hint 由服务端下发，界面不猜数字含义）。 */
export interface JobDetailStat {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly hint: string;
}

/** 一个执行阶段：叫什么、做了多少、花了多久。 */
export interface JobDetailStage {
  readonly key: string;
  readonly label: string;
  readonly ms: number;
  readonly count: number | null;
  readonly note: string | null;
}

/**
 * 执行明细（手写镜像 server/services/jobs.ts 的 JobRunDetail）。
 * 上线前写下的记录没有 version（v1 裸字段），界面对那部分要降级显示。
 */
export interface JobRunDetail {
  readonly version: number;
  readonly outcome: 'running' | 'skipped' | 'ok' | 'partial' | 'failed';
  readonly note: string;
  readonly window: { readonly from: string; readonly to: string } | null;
  readonly stats: readonly JobDetailStat[];
  readonly stages: readonly JobDetailStage[];
  readonly errors: readonly string[];
  readonly skipped?: string;
  readonly meetings?: number;
  readonly reconciled?: number;
  readonly artifactHealed?: number;
  readonly error?: string;
}

export interface JobRunRow {
  readonly jobName: string;
  readonly periodKey: string;
  readonly status: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  /** 开始到结束的毫秒数；仍在跑为 null（界面自己按当前时间算已跑多久）。 */
  readonly durationMs: number | null;
  readonly detail: unknown;
}

export interface AuditEntry {
  readonly id: string;
  readonly actor: string | null;
  readonly action: string;
  readonly target: string | null;
  readonly detail: unknown;
  readonly createdAt: string;
}

// ── 会议 ───────────────────────────────────────────────────

export type MeetingSource = 'tencent' | 'feishu' | 'manual';
export type MeetingStatus = 'scheduled' | 'ongoing' | 'ended' | 'cancelled';

export interface MeetingRow {
  readonly id: string;
  readonly source: MeetingSource;
  readonly title: string;
  readonly description: string | null;
  readonly startAt: string;
  readonly endAt: string | null;
  readonly organizerName: string | null;
  readonly organizerUsername: string | null;
  readonly isMine: boolean;
  readonly status: MeetingStatus;
  readonly participantCount: number;
  readonly artifactCount: number;
  readonly artifactTypes: readonly string[];
}

/** 数据新鲜度（由查询接口顺带返回）：陈旧表示定时同步可能已停。 */
export interface DataFreshness {
  readonly lastSyncedAt: string | null;
  readonly ageSeconds: number | null;
  readonly stale: boolean;
  readonly syncedNow: boolean;
  readonly syncError: string | null;
}

/** 会议数据范围三档，从宽到窄。dept = 本人担任负责人的部门里所有成员的会议。 */
export type MeetingScope = 'own' | 'dept' | 'all';

export interface MeetingList {
  readonly meetings: readonly MeetingRow[];
  readonly total: number;
  readonly scope: MeetingScope;
  /** 台账数据的新鲜度（可选：老版本响应没有这段）。 */
  readonly freshness?: DataFreshness;
}

/** 一次进出会议的时间段。同一人多次进出会有多段。 */
export interface ParticipantSegment {
  readonly joinAt: string | null;
  readonly leaveAt: string | null;
}

export interface MeetingParticipant {
  readonly displayName: string;
  readonly username: string | null;
  /** 首次入会（各段里最早的）。 */
  readonly joinAt: string | null;
  /** 最晚离会（各段里最晚的）。 */
  readonly leaveAt: string | null;
  /** 真实参会时长 = 各段之和（不含中途离场的空档）。 */
  readonly durationSec: number | null;
  /** true = 有段没记录离会，此时 durationSec 只是下界。 */
  readonly partial: boolean;
  /** 该人的每一次进出（按入会时间升序）。 */
  readonly segments: readonly ParticipantSegment[];
}

export interface MeetingArtifact {
  readonly id: string;
  readonly type: 'recording' | 'transcript' | 'minutes' | 'summary' | 'recording_download';
  readonly title: string;
  readonly url: string | null;
  readonly content: string | null;
}

export interface MeetingDetail {
  readonly id: string;
  readonly source: MeetingSource;
  readonly title: string;
  readonly description: string | null;
  readonly startAt: string;
  readonly endAt: string | null;
  readonly organizerName: string | null;
  readonly organizerUsername: string | null;
  readonly status: MeetingStatus;
  readonly createdByChannel: string;
  readonly canCancel: boolean;
  /** 产物采集失败的原因（如飞书纪要 403 应用无权限）；null = 正常。 */
  readonly artifactError: string | null;
  /** 录制文件长度（秒）；null = 没有录制或没采到。**不是会议时长**。 */
  readonly recordingDurationSec: number | null;
  readonly participants: readonly MeetingParticipant[];
  readonly artifacts: readonly MeetingArtifact[];
}

// ── 集成凭据（设置页）───────────────────────────────────────

/** 腾讯会议企业通讯录成员（只读视图）。 */
export interface TencentUser {
  readonly userid: string;
  readonly username: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly roleName: string | null;
  readonly status: string;
  readonly accountVersion: string | null;
}

export interface TencentUsersResult {
  readonly users: readonly TencentUser[];
  /** 全量成员数（过滤前）。 */
  readonly total: number;
  /** 本次关键词命中的条数。 */
  readonly matched: number;
  readonly fetchedAt: string;
  readonly cached: boolean;
}

/** 飞书企业通讯录成员（持久化视图，从 feishu_user 表读，不再触网）。 */
export interface FeishuUser {
  readonly user_id: string;
  readonly open_id: string;
  readonly union_id: string;
  readonly name: string;
  readonly en_name: string | null;
  readonly avatar_url: string | null;
  readonly mobile_visible: boolean;
  /** 部门中文路径（"/" 拼接），例如"公司 / 广告部 / 销售部"。 */
  readonly department_path: string | null;
  /** 部门 open_department_id 数组。 */
  readonly departments?: readonly string[];
}

export interface FeishuUsersResult {
  readonly users: readonly FeishuUser[];
  readonly total: number;
  readonly matched: number;
  readonly fetchedAt: string;
  readonly cached: boolean;
  /** 数据库状态：是否有数据、总人数、最近同步时间——前端用来决定要不要显示「同步」按钮 */
  readonly dbStatus: {
    readonly has_data: boolean;
    readonly total: number;
    readonly last_synced_at: string | null;
  };
}

/** 同步状态机（GET /sync/status 返回）。 */
export type SyncPhase =
  | 'idle'
  | 'starting'
  | 'fetching_departments'
  | 'fetching_users'
  | 'saving'
  | 'done'
  | 'failed';

export interface FeishuSyncState {
  readonly running: boolean;
  readonly phase: SyncPhase | string;
  readonly startedAt?: string;
  readonly finishedAt?: string;
  /** fetching_departments 阶段填充：当前 BFS 深度 / 已扫到的部门数 */
  readonly depth?: number;
  readonly deptCount?: number;
  /** fetching_users 阶段填充 */
  readonly current?: number;
  readonly total?: number;
  readonly deptName?: string;
  /** saving 阶段填充 */
  readonly count?: number;
  /** done 阶段填充 */
  readonly result?: { totalUsers: number; totalDepts: number };
  /** failed 阶段填充 */
  readonly error?: string;
}

export interface FeishuSyncStatusResult {
  readonly sync: FeishuSyncState;
  readonly db: {
    readonly has_data: boolean;
    readonly total: number;
    readonly last_synced_at: string | null;
  };
}

/** 我的会议多维表格绑定（服务端只记链接与坐标）。 */
export interface BitableBindingView {
  readonly bound: boolean;
  readonly binding: {
    readonly app_token: string;
    readonly table_id: string;
    readonly url: string | null;
    readonly last_synced_at: string | null;
  } | null;
  readonly fields: readonly {
    readonly name: string;
    readonly type: string;
    readonly required: boolean;
    readonly note: string;
    readonly options?: readonly string[];
  }[];
  readonly hint: string;
}

export interface CredentialView {
  readonly configured: boolean;
  readonly fields: Readonly<Record<string, string | null>>;
  readonly updatedBy: string | null;
  readonly updatedAt: string | null;
}

/** 本人飞书授权状态（/api/v1/feishu/oauth/status）。不含任何 token 形态数据。 */
export interface FeishuOauthStatus {
  readonly authorized: boolean;
  readonly openId: string | null;
  readonly feishuName: string | null;
  readonly scope: string | null;
  readonly accessExpiresAt: string | null;
  readonly refreshExpiresAt: string | null;
  readonly updatedAt: string | null;
  readonly needsReauth: boolean;
  readonly redirect_uri_configured: boolean;
  readonly configured_scopes: readonly string[];
}

export const api = {
  // 认证
  login: (username: string, password: string) =>
    post<AuthResult>('/api/v1/auth/login', { username, password }),
  register: (username: string, password: string, displayName?: string) =>
    post<AuthResult>('/api/v1/auth/register', { username, password, displayName }),
  logout: () => post<{ ok: true }>('/api/v1/auth/logout', {}),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true }>('/api/v1/auth/change-password', { currentPassword, newPassword }),
  me: () => get<{ user: AuthUser; kind: string }>('/api/v1/me'),

  // 账号安全
  sessions: () => get<SessionList>('/api/v1/auth/sessions'),
  revokeSession: (fingerprint: string) =>
    post<{ revoked: boolean }>('/api/v1/auth/sessions/revoke', { fingerprint }),
  apiTokens: () => get<{ tokens: readonly ApiTokenRow[] }>('/api/v1/auth/tokens'),
  createApiToken: () => post<{ token: string; expiresAt: string }>('/api/v1/auth/tokens', {}),
  revokeApiToken: (fingerprint: string) =>
    post<{ revoked: boolean }>('/api/v1/auth/tokens/revoke', { fingerprint }),
  loginLog: (limit = 50) =>
    get<{ entries: readonly LoginLogEntry[] }>(`/api/v1/auth/login-log?limit=${limit}`),

  // 管理
  adminUsers: () => get<{ users: readonly ManagedUser[] }>('/api/v1/admin/users'),
  adminCreateUser: (username: string, role: string, displayName?: string) =>
    post<{ username: string; initialPassword: string }>('/api/v1/admin/users', {
      username,
      role,
      displayName,
    }),
  adminSetUserRole: (username: string, role: string) =>
    post<{ ok: true }>('/api/v1/admin/users/role', { username, role }),
  adminBindTencent: (username: string, tencentUserId: string | null) =>
    post<{ ok: true }>('/api/v1/admin/users/tencent', { username, tencentUserId }),
  // 飞书账号绑定（管理员帮成员绑）：绑定后该成员的「我的会议」能匹配飞书台账
  adminBindFeishu: (username: string, feishuOpenId: string | null) =>
    post<{ ok: true }>('/api/v1/admin/users/feishu', { username, feishuOpenId }),
  adminSetUserStatus: (username: string, status: 'active' | 'disabled') =>
    post<{ ok: true }>('/api/v1/admin/users/status', { username, status }),
  adminRoles: () =>
    get<{ roles: readonly RoleRow[]; permissions: readonly PermissionDef[] }>(
      '/api/v1/admin/roles',
    ),
  adminCreateRole: (name: string, description?: string) =>
    post<{ ok: true }>('/api/v1/admin/roles', { name, description }),
  adminSetRolePerms: (role: string, permissions: readonly string[]) =>
    post<{ ok: true }>('/api/v1/admin/roles/perms', { role, permissions }),

  // 部门与部门负责人。列表对管理员与部门负责人开放（view 字段说明看到的是全量还是自己管的），
  // 写操作要求 department:manage（管理员）。
  departments: () =>
    get<{ departments: readonly DepartmentRow[]; view: 'all' | 'led' }>('/api/v1/departments'),
  adminCreateDepartment: (name: string, description?: string) =>
    post<{ ok: true; id: string }>('/api/v1/admin/departments', { name, description }),
  adminUpdateDepartment: (input: { name: string; newName?: string; description?: string }) =>
    post<{ ok: true }>('/api/v1/admin/departments/update', input),
  adminDeleteDepartment: (name: string) =>
    post<{ ok: true; membersRemoved: number }>('/api/v1/admin/departments/delete', { name }),
  adminSetDepartmentMember: (department: string, username: string, add: boolean) =>
    post<{ ok: true; isLeader: boolean }>('/api/v1/admin/departments/member', {
      department,
      username,
      add,
    }),
  adminSetDepartmentLeader: (department: string, username: string, leader: boolean) =>
    post<{ ok: true; stillLeads: boolean }>('/api/v1/admin/departments/leader', {
      department,
      username,
      leader,
    }),
  adminConfig: () => get<ConfigView>('/api/v1/admin/config'),
  adminSaveConfig: (entries: readonly { key: string; value: unknown }[]) =>
    post<{ ok: true }>('/api/v1/admin/config', { entries }),
  adminAudit: (limit = 50) =>
    get<{ entries: readonly AuditEntry[] }>(`/api/v1/admin/audit?limit=${limit}`),
  adminJobs: () => get<{ jobs: readonly JobRunRow[] }>('/api/v1/admin/jobs'),
  adminRunJob: (name: string) =>
    post<{ executed: boolean; detail?: JobRunDetail; error?: string }>('/api/v1/admin/jobs/run', {
      name,
    }),

  // 会议
  meetings: (params: {
    from?: string;
    to?: string;
    q?: string;
    source?: string;
    status?: string;
    limit?: number;
    offset?: number;
    /** true = 数据陈旧时等服务端补同步完成再返回（「立即同步」用）。 */
    refresh?: boolean;
    /** true = 每场会议带上参会人姓名列表（导出到表格等场景用）。 */
    withParticipants?: boolean;
    /** 按哪个成员查（admin/manager 才能传他人的 username）。 */
    username?: string;
    /** 在 username 视角下过滤身份：organizer / participant / any */
    as?: 'organizer' | 'participant' | 'any';
  }) => {
    const sp = new URLSearchParams();
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (params.q) sp.set('q', params.q);
    if (params.source) sp.set('source', params.source);
    if (params.status) sp.set('status', params.status);
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    if (params.offset !== undefined) sp.set('offset', String(params.offset));
    if (params.refresh === true) sp.set('refresh', '1');
    if (params.withParticipants === true) sp.set('with_participants', '1');
    if (params.username !== undefined) sp.set('username', params.username);
    if (params.as !== undefined) sp.set('as', params.as);
    const qs = sp.toString();
    return get<MeetingList>(`/api/v1/meetings${qs === '' ? '' : `?${qs}`}`);
  },
  meeting: (id: string) => get<MeetingDetail>(`/api/v1/meeting?id=${encodeURIComponent(id)}`),
  createMeeting: (input: {
    title: string;
    startAt: string;
    endAt?: string;
    description?: string;
    source: MeetingSource;
  }) => post<{ id: string }>('/api/v1/meetings', input),
  cancelMeeting: (id: string) => post<{ ok: true }>('/api/v1/meetings/cancel', { id }),
  // 腾讯录制 MP4 下载：服务端现取带签名直链（会过期，不能预生成）
  recordingDownload: (id: string) =>
    get<{ url: string }>(`/api/v1/meeting/recording-download?id=${encodeURIComponent(id)}`),

  // 腾讯通讯录（需 tencent:user:list）
  tencentUsers: (params: { q?: string; refresh?: boolean } = {}) => {
    const sp = new URLSearchParams();
    if (params.q !== undefined && params.q !== '') sp.set('q', params.q);
    if (params.refresh === true) sp.set('refresh', '1');
    const qs = sp.toString();
    return get<TencentUsersResult>(`/api/v1/tencent/users${qs === '' ? '' : `?${qs}`}`);
  },

  // 飞书通讯录（需 feishu:user:list）—— 从本地 feishu_user 表读，不触网
  feishuUsers: (params: { q?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.q !== undefined && params.q !== '') sp.set('q', params.q);
    const qs = sp.toString();
    return get<FeishuUsersResult>(`/api/v1/feishu/users${qs === '' ? '' : `?${qs}`}`);
  },
  // 飞书通讯录同步状态（轮询用）
  feishuSyncStatus: () => get<FeishuSyncStatusResult>('/api/v1/feishu/users/sync/status'),
  // 触发飞书通讯录同步（fire-and-forget，立即返回）
  feishuSync: () =>
    post<{ accepted: boolean; state: FeishuSyncState }>('/api/v1/feishu/users/sync', {}),

  // 我的会议多维表格绑定（个人设置；服务端只记链接，表格内容由助手写入）
  // 飞书用户授权（本人自助）
  feishuOauthStatus: () => get<FeishuOauthStatus>('/api/v1/feishu/oauth/status'),
  feishuOauthStart: () =>
    post<{ authorize_url: string; redirect_uri: string; state_expires_at: string }>(
      '/api/v1/feishu/oauth/start',
      {},
    ),
  feishuOauthRevoke: () =>
    post<{ ok: true; removed: boolean; note: string }>('/api/v1/feishu/oauth/revoke', {}),

  meBitable: () => get<BitableBindingView>('/api/v1/me/bitable'),
  saveMeBitable: (input: {
    app_token?: string;
    table_id?: string;
    url?: string;
    mark_synced?: boolean;
  }) => post<{ ok: true; binding: BitableBindingView['binding'] }>('/api/v1/me/bitable', input),
  clearMeBitable: () => post<{ ok: true }>('/api/v1/me/bitable/clear', {}),

  // 集成凭据
  tencentCreds: () => get<CredentialView>('/api/v1/admin/integrations/tencent'),
  saveTencentCreds: (input: {
    corpId?: string;
    sdkId?: string;
    secretId?: string;
    secretKey?: string;
    operatorUsername?: string;
  }) => post<CredentialView>('/api/v1/admin/integrations/tencent', input),
  feishuCreds: () => get<CredentialView>('/api/v1/admin/integrations/feishu'),
  saveFeishuCreds: (input: { appId?: string; appSecret?: string }) =>
    post<CredentialView>('/api/v1/admin/integrations/feishu', input),
};

// ── 会话存储 ───────────────────────────────────────────────

export function saveSession(r: AuthResult): void {
  try {
    localStorage.setItem(TOKEN_KEY, r.token);
  } catch {
    /* 隐私模式忽略 */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* 隐私模式忽略 */
  }
}

export function hasSession(): boolean {
  try {
    return localStorage.getItem(TOKEN_KEY) !== null;
  } catch {
    return false;
  }
}
